import * as ROUTES from '@/shared/constants/routes';
import { hashPassword } from '@chikara/utils';
import { useState, useCallback, useMemo, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { passwordResetService } from '@/features/auth/services/passwordResetService';
import { useSpinner } from '@chikara/ui';
import { useAsyncGuard } from '@chikara/hooks';
import { t } from '@/i18n';

const PASSWORD_MIN_LENGTH = 8;

export const useResetPassword = (onSuccess?: () => void) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { show, hide, isSpinnerLoading } = useSpinner();

  const token = searchParams.get('reference');

  const [passwordToken, setPasswordToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const { checkGuard: summaryGuard, resetGuard: resetSummaryGuard } = useAsyncGuard(1000);

  // Password validation rules
  const isLengthValid = password.length >= PASSWORD_MIN_LENGTH;
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);

  const isValidPassword = isLengthValid && hasNumber && hasSpecial && hasUppercase;
  const isMatchValid = password.length > 0 && password === confirmPassword;
  const isFormValid = isValidPassword && isMatchValid && !isSpinnerLoading;

  const passwordError = useMemo(() => {
    if (confirmPassword.length > 0 && !isMatchValid) {
      return t('resetPassword.passwordMismatch');
    }
    return undefined;
  }, [confirmPassword, isMatchValid]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value.replace(/\s/g, ''));
    if (apiError) setApiError(null);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value.replace(/\s/g, ''));
    if (apiError) setApiError(null);
  };

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !token || !summaryGuard()) return;

    setApiError(null);
    show();

    try {
      const hashedPassword = await hashPassword(password);
      await passwordResetService.confirmPasswordReset({
        token: passwordToken || token,
        new_password: hashedPassword,
      });

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(ROUTES.LOGIN, {
          state: { toast: t('resetPassword.success') },
        });
      }
    } catch (err: any) {
      const status = err?.status || err?.response?.status;
      if (err.code !== 'PASSWORD_ALREADY_USED' && (status === 400 || status === 410)) {
        setIsTokenExpired(true);
      } else {
        setApiError(err.message || t('resetPassword.error'));
      }
      resetSummaryGuard();
    } finally {
      hide();
    }
  }, [isFormValid, token, password, show, hide, navigate, onSuccess, summaryGuard, resetSummaryGuard]);

  const goToForgotPassword = () => {
    navigate(ROUTES.FORGOT_PASSWORD);
  };

  const validateToken = async (token: string) => {
    show();
    if (!token) {
      setApiError(t('onboarding.error.invalidToken'));
      hide();
      return;
    }

    if (!summaryGuard()) {
      hide();
      return;
    }

    try {
      const response = await passwordResetService.validateToken(token);
      if (response.data.data.valid && response?.data.data.password_token) {
        setPasswordToken(response.data.data.password_token);
      }

      setApiError(null);
    } catch (err: any) {
      setApiError(t('onboarding.error.subtitle'));
    } finally {
      hide();
    }
  };

  return {
    password,
    confirmPassword,
    passwordError,
    apiError,
    isLengthValid,
    hasNumber,
    hasSpecial,
    hasUppercase,
    isFormValid,
    isTokenExpired,
    isLoading: isSpinnerLoading,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleSubmit,
    goToForgotPassword,
    validateToken
  };
};
